import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { graphGet } from '../api/graphClient';

export interface Person {
  aadObjectId: string;
  displayName: string;
  mail?: string | null;
  userPrincipalName?: string | null;
}

interface Props {
  pca: IPublicClientApplication;
  value: Person | null;
  onChange: (person: Person | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

interface GraphUser {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string | null;
}

const AVATAR_COLORS = ['#0E7490', '#7C3AED', '#B45309', '#065F46', '#9D174D'];

function initials(name?: string | null): string {
  if (!name) return '?';
  return name.split(/\s+/).slice(0, 2).map(s => s[0] ?? '').join('').toUpperCase();
}

function avatarColor(name?: string | null): string {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function Avatar({ name, size = 22 }: { name?: string | null; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: avatarColor(name), color: '#fff',
      fontSize: size <= 22 ? 10 : 11, fontWeight: 600,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {initials(name)}
    </div>
  );
}

// The results list renders in a body-level portal: anchored inside the field
// it would extend the scrollHeight of any scrollable modal card containing it
// (absolutely-positioned children still count toward ancestor overflow),
// which grew the modal and tangled its scroll with the list's.
const DROPDOWN_MAX_H = 200;

interface DropdownPlacement {
  left: number;
  width: number;
  top?: number;    // anchored below the field
  bottom?: number; // flipped above the field (distance from viewport bottom)
}

export default function PeoplePicker({ pca, value, onChange, placeholder = 'Search people…', disabled }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GraphUser[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [placement, setPlacement] = useState<DropdownPlacement | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounce = useRef<number | null>(null);

  const showList = open && query.trim().length >= 2;

  // Close on outside click — the portaled list is outside wrapRef, so it
  // must be checked separately or selecting a result would close first.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Position the portaled list against the field, flipping upward when the
  // field sits too close to the bottom of the viewport. Re-anchors on any
  // scroll (capture phase catches modal-internal scrolling) and on resize.
  const position = useCallback(() => {
    const anchor = wrapRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const flipUp = spaceBelow < DROPDOWN_MAX_H + 12 && r.top > spaceBelow;
    setPlacement(flipUp
      ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4 }
      : { left: r.left, width: r.width, top: r.bottom + 4 });
  }, []);

  useLayoutEffect(() => {
    if (!showList) { setPlacement(null); return; }
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [showList, position]);

  // Search on query change
  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounce.current = window.setTimeout(async () => {
      try {
        const encoded = encodeURIComponent(`"displayName:${q}"`);
        const data = await graphGet<{ value: GraphUser[] }>(
          pca,
          `/users?$search=${encoded}&$select=id,displayName,mail,userPrincipalName&$top=8&$orderby=displayName`,
          { ConsistencyLevel: 'eventual' },
        );
        setResults(data.value ?? []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { if (debounce.current) window.clearTimeout(debounce.current); };
  }, [query, pca]);

  const handleSelect = (u: GraphUser) => {
    onChange({ aadObjectId: u.id, displayName: u.displayName, mail: u.mail, userPrincipalName: u.userPrincipalName });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setResults([]);
  };

  // Selected state (shown when no active query)
  if (value && !open) {
    return (
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <div
          onClick={() => { if (!disabled) setOpen(true); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 10px',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--surface)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            minHeight: 34,
          }}
        >
          <Avatar name={value.displayName} />
          <div style={{ flex: 1, minWidth: 0 }} title={value.mail ?? value.userPrincipalName ?? undefined}>
            <div style={{ fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value.displayName}</div>
            {(value.mail ?? value.userPrincipalName) && (
              <div style={{ fontSize: 11, color: 'var(--ink-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {value.mail ?? value.userPrincipalName}
              </div>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); handleClear(); }}
              aria-label="Clear"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--ink-4)', fontSize: 16, padding: 0, lineHeight: 1,
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink-2)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-4)')}
            >×</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); (e.target as HTMLInputElement).blur(); } }}
        placeholder={value?.displayName ?? placeholder}
        disabled={disabled}
      />
      {showList && placement && createPortal(
        <div ref={listRef} style={{
          position: 'fixed',
          left: placement.left, width: placement.width,
          ...(placement.top !== undefined ? { top: placement.top } : { bottom: placement.bottom }),
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          boxShadow: 'var(--shadow-panel)',
          maxHeight: DROPDOWN_MAX_H, overflowY: 'auto',
          // Wheel events at the list edges must not chain into the modal.
          overscrollBehavior: 'contain',
          // BoardModal overlay sits at zIndex 250 — the list must clear it.
          zIndex: 300,
        }}>
          {loading && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-4)' }}>Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-4)' }}>No matches.</div>
          )}
          {!loading && results.map(u => (
            <div
              key={u.id}
              onClick={() => handleSelect(u)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', cursor: 'pointer',
                borderBottom: '0.5px solid var(--border-light)',
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Avatar name={u.displayName} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.displayName}
                </div>
                {(u.mail ?? u.userPrincipalName) && (
                  <div style={{ fontSize: 11, color: 'var(--ink-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.mail ?? u.userPrincipalName}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
